import {
  infiniteQueryOptions,
  queryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';
import { useAccount } from 'wagmi';
import { WS_INVALIDATED_QUERY_STALE_TIME } from '../constants';
import { operations, SchemaPositionDto } from '../generated/apiTypes';
import { dataOrThrow, fetchClient } from '../lib/apiClient';
import { Gwei } from '../lib/gwei';
import { safeDivide } from '../lib/math';
import { removeUndefined } from '../lib/objectHelpers';
import { PrettyMerge } from '../lib/tsUtilityTypes';
import { useFlattenedPages } from './useFlattenedPages';

const transformPosition = (position: SchemaPositionDto) => {
  const cost = Gwei.toGwei(position.cost);
  const size = Gwei.toGwei(position.size);
  return {
    ...position,
    cost,
    avgPrice: !cost || !size ? 0n : Gwei.abs(safeDivide(cost, size)),
    size,
    fundingUsd: Gwei.toGwei(position.fundingUsd),
    totalIncreaseNotional: Gwei.toGwei(position.totalIncreaseNotional),
    totalDecreaseNotional: Gwei.toGwei(position.totalDecreaseNotional),
    totalIncreaseQuantity: Gwei.toGwei(position.totalIncreaseQuantity),
    totalDecreaseQuantity: Gwei.toGwei(position.totalDecreaseQuantity),
    realizedPnl: Gwei.toGwei(position.realizedPnl),
    feesAccruedUsd: Gwei.toGwei(position.feesAccruedUsd),
  };
};
export type Position = ReturnType<typeof transformPosition>;

export type PositionParams = PrettyMerge<
  Omit<operations['PositionController_list']['parameters']['query'], 'cursor'>,
  {
    subaccountId: string | undefined;
  }
>;

export const positionsQuery = ({ subaccountId, ...params }: PositionParams) =>
  infiniteQueryOptions({
    queryKey: ['GET', '/v1/position', { subaccountId }, params] as const,
    initialPageParam: '',
    enabled: Boolean(subaccountId),
    queryFn: async ({ pageParam, queryKey, signal }) => {
      if (!subaccountId) return { data: [], hasNext: false, nextCursor: undefined };
      const [method, url] = queryKey;
      const { data } = await fetchClient[method](url, {
        signal,
        params: removeUndefined({
          query: {
            ...params,
            subaccountId,
            open: params.open || false,
            cursor: pageParam || undefined,
          },
        }),
      }).then(dataOrThrow);

      const positions = data?.data.map(transformPosition) ?? [];

      return {
        data: positions,
        hasNext: data?.hasNext || false,
        nextCursor: data?.nextCursor,
      };
    },
    staleTime: WS_INVALIDATED_QUERY_STALE_TIME,
    getNextPageParam: (lastPage) => (lastPage.hasNext ? lastPage.nextCursor : undefined),
  });

export const usePositions = ({ subaccountId, ...params }: PositionParams) => {
  const { address } = useAccount();
  const subaccountIdToUse = address ? subaccountId : undefined;
  const { data, error, isFetching, isPending } = useInfiniteQuery(
    positionsQuery({ ...params, subaccountId: subaccountIdToUse, open: true })
  );
  const flatData = useFlattenedPages('data', data?.pages);
  return { data: flatData, error, isFetching, isPending };
};

export const useClosedPositions = ({ subaccountId, ...params }: PositionParams) => {
  const { address } = useAccount();
  const subaccountIdToUse = address ? subaccountId : undefined;
  return useInfiniteQuery(
    positionsQuery({
      orderBy: 'updatedAt',
      ...params,
      subaccountId: subaccountIdToUse,
      open: false,
    })
  );
};

export const positionByIdQuery = (id: string | undefined) =>
  queryOptions({
    queryKey: ['GET', `/v1/position/{id}`, id] as const,
    enabled: Boolean(id),
    queryFn: async ({ queryKey, signal }) => {
      if (!id) return null;
      const [method, url] = queryKey;
      const { data } = await fetchClient[method](url, {
        signal,
        params: {
          path: { id },
        },
      }).then(dataOrThrow);
      return data ? transformPosition(data) : null;
    },
    staleTime: WS_INVALIDATED_QUERY_STALE_TIME,
  });

export const usePositionById = (id: string | undefined) => {
  return useQuery(positionByIdQuery(id));
};
